import { useState, useEffect } from "react";

export default function AddTeamsForm({ apiUrl, accessToken }) {
  const [teams, setTeams] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingTeamName, setEditingTeamName] = useState("");

  // Fetch existing teams
  const fetchTeams = async () => {
    try {
      const res = await fetch(`${apiUrl}/teams`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      setTeams(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch teams:", err);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  // Add a new team
  const handleAddTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return alert("Enter a team name");

    try {
      await fetch(`${apiUrl}/teams`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ name: newTeamName.trim() }),
      });
      setNewTeamName("");
      fetchTeams();
    } catch (err) {
      console.error("Failed to add team:", err);
    }
  };

  // Save edited team name
  const handleSaveTeam = async (teamId) => {
    if (!editingTeamName.trim()) return alert("Enter a team name");

    try {
      const res = await fetch(`${apiUrl}/teams/${teamId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ name: editingTeamName.trim() }),
      });
      if (!res.ok) throw new Error("Failed to update team");
      const updatedTeam = await res.json();

      // Update teams state
      setTeams((prev) =>
        prev.map((t) => (t.id === updatedTeam.id ? updatedTeam : t))
      );

      // Clear editing state
      setEditingTeamId(null);
      setEditingTeamName("");
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="max-w-lg mx-auto bg-gray-100 p-4 rounded shadow">
      <h2 className="text-xl font-bold mb-4">Manage Teams</h2>

      {/* Add team form */}
      <form onSubmit={handleAddTeam} className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="New team name"
          value={newTeamName}
          onChange={(e) => setNewTeamName(e.target.value)}
          className="flex-1 border p-2 rounded"
        />
        <button type="submit" className="bg-green-500 text-black px-4 py-2 rounded">
          Add
        </button>
      </form>

      {/* Existing teams */}
      <div>
        <h3 className="font-semibold mb-2">Existing Teams:</h3>
        {teams.length > 0 ? (
          <ul className="list-disc list-inside text-gray-700">
            {teams.map((team) => (
              <li key={team.id} className="flex items-center justify-between gap-4">
                {editingTeamId === team.id ? (
                  <>
                    <input
                      type="text"
                      value={editingTeamName}
                      onChange={(e) => setEditingTeamName(e.target.value)}
                      className="border p-1 rounded flex-1"
                    />
                    <button
                      className="bg-blue-500 text-black px-2 py-1 rounded text-sm"
                      onClick={() => handleSaveTeam(team.id)}
                    >
                      Save
                    </button>
                    <button
                      className="bg-gray-400 text-black px-2 py-1 rounded text-sm"
                      onClick={() => setEditingTeamId(null)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1">{team.name}</span>
                    <button
                      className="
                        bg-yellow-400 text-black
                        px-3 py-1.5
                        rounded-md
                        text-sm font-medium
                        shadow-sm
                        hover:bg-yellow-500
                        focus:outline-none focus:ring-2 focus:ring-yellow-300
                      "
                      onClick={() => {
                        setEditingTeamId(team.id);
                        setEditingTeamName(team.name);
                      }}
                    >
                      Edit
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No teams available.</p>
        )}
      </div>
    </div>
  );
}
